import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/header/header';
import { motion, AnimatePresence } from "motion/react"
import './landpage.scss';
import emailjs from '@emailjs/browser';

const heroSlides = [
  {
    src: '/assets/hero/divine-completion-november.jpeg',
    alt: 'RCCG Amazing Grace Chapel November 2026 theme: Divine Completion',
    format: 'landscape',
  },
  {
    src: '/assets/hero/jesus-encounter-november.jpeg',
    alt: 'Jesus Encounter at RCCG Amazing Grace Chapel, November 5 through 7, 2026',
    format: 'portrait',
  },
];

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [popupType, setPopupType] = useState('success');
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);

  useEffect(() => {
    const slideTimer = window.setInterval(() => {
      setActiveHeroSlide((currentSlide) => (currentSlide + 1) % heroSlides.length);
    }, 5000);

    return () => window.clearInterval(slideTimer);
  }, []);

  useEffect(() => {
    // Scroll animation observer
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, observerOptions);

    // Observe all sections with fade-in class
    document.querySelectorAll('.fade-in-section').forEach(el => {
      observer.observe(el);
    });

    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);

  // Modal handlers
  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen]);

  // Handle prayer request submission
  const handlePrayerSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    emailjs
      .sendForm(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        e.target,
        process.env.REACT_APP_PUBLIC_KEY
      )
      .then(
        () => {
          setPopupType('success');
          setPopupMessage('Your prayer request has been submitted. Our prayer team will lift you up in prayer.');
          setIsSubmitting(false);
          e.target.reset();
        },
        () => {
          setPopupType('error');
          setPopupMessage('Something went wrong. Please try again later.');
          setIsSubmitting(false);
        }
      );
  };

  const closePopup = () => setPopupMessage(null);

  return (
    <div className="home">
      <Header />
    <div className='home-container'>
      {/* Hero Section */}
      <section className="hero11">
        <div className="hero-carousel11" aria-label="Featured RCCG Amazing Grace Chapel announcements">
          {heroSlides.map((slide, index) => (
            <img
              key={slide.src}
              src={slide.src}
              alt={slide.alt}
              aria-hidden={index !== activeHeroSlide}
              className={`hero-slide11 hero-slide11--${slide.format}${index === activeHeroSlide ? ' is-active' : ''}`}
            />
          ))}
        </div>
        <div className="hero-content11">
          {/* <h1>Light for the city & beyond</h1>
          <p>We are a community compelled by a Jesus-centered vision to transform lives. One encounter with God can completely change everything.</p> */}
          {/* <Link to="/visit" className="hero-cta">Learn about RCCG AGC</Link> */}
        </div>
      </section>

      {/* About Section - Scale + Fade */}
      <motion.section 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        // whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="section1" 
      >
        <div className="section-header1">
          <h2 className="section-title1">Welcome to RCCG - Amazing Grace Chapel</h2>
          <p className="section-subtitle1">We believe that one encounter with God can completely transform everything. Our church is all about empowering people to gather and encouraging them to make an impact wherever they go.</p>
        </div>
      </motion.section>

      {/* From the Pastor's Desk Section - Slide from left */}
      <motion.section 
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className='add'
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 4%' }}>
          <div className="split-section1" style={{ gap: '5rem' }}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="split-image1 l-height" 
              // style={{ height: '500px', borderRadius: '4px' }}
            >
              <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1770505291/6Y5A4140_dp68bq.jpg" alt="Pastor Charles Salako" style={{ width: '100%', height: '100%', borderRadius: '8px', objectFit: 'cover', objectPosition: 'top center' }} />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="split-content1"
            >
              <p style={{ color: 'var(--gray-600)', fontSize: '0.95rem', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '1rem' }}>From the Pastor's Desk</p>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: '1.5rem', letterSpacing: '-1px', lineHeight: 1.2 }}>A Word of Welcome</h2>
              <p className='land-p'>
                Welcome to RCCG Amazing Grace Church! Whether you're visiting for the first time or have been part of our family for years, we're so glad you're here.
              </p>
              <p className='land-p'>
                Our prayer is that you would encounter the transforming love of Jesus and find a community where you can grow, serve, and make a lasting impact for the Kingdom of God.
              </p>
              <p className='land-p'>
                I look forward to meeting you and walking together in faith.
              </p>
              <p className='land-p2'>
                Pastor Charles & Adeola Salako<br />
                <span style={{ color: 'var(--gray-500)', fontSize: '0.95rem', fontWeight: 400 }}>Lead Pastors</span>
              </p>
              <Link to="/leadership" className="card-link" style={{ fontSize: '1.05rem' }}>Meet our leadership team</Link>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Cards Section - Stagger animation */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6 }}
        className="section"
      >
        <div className="card-grid">
          {[
            {
              img: "https://res.cloudinary.com/dsnontai6/image/upload/v1771179684/0A3A3884_q2gxzz.jpg",
              title: "Plan Your Visit",
              text: "Find gathering times, directions, and everything you need to know.",
              link: "/visit",
              position: "top center"
            },
            {
              img: "https://res.cloudinary.com/dsnontai6/image/upload/v1770242065/0A3A0852_eit94n.jpg",
              title: "What's Coming Up",
              text: "Check out what's coming up and register for upcoming events in our community.",
              link: "/upcoming-events",
              position: "center"
            },
            {
              img: "https://res.cloudinary.com/dsnontai6/image/upload/v1770242064/6Y5A3483_jemu9u.jpg",
              title: "I'm New",
              text: "We can't wait to meet you! Learn more about your next steps and how to get connected.",
              link: "/new",
              position: "center"
            }
          ].map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="card"
            >
              <div className="card-image">
                <img src={card.img} alt='pic' style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: card.position }} />
              </div>
              <div className="card-content">
                <h3 className="card-title">{card.title}</h3>
                <p className="card-text">{card.text}</p>
                <Link to={card.link} className="card-link">Learn more</Link>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Messages Section - Slide from right */}
      <motion.section 
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{ padding: '4rem 0', background: 'var(--black)', width: '100%', margin: 0 }}
      >
        <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0 4%' }}>
          <div className="split-section1" style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="split-content1" 
              style={{ color: 'var(--white)' }}
            >
              <h2 className='msg-h'>Messages</h2>
              <p className='msg-p'>Catch up on the latest message from our church and explore some of our core values.</p>
              <Link to="https://www.youtube.com/@agcmiddletown" target="_blank" className="message-link">Learn more</Link>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="split-image1" 
              style={{ height: '500px', background: 'var(--gray-800)', borderRadius: '0px', overflow: 'hidden' }}
            >
              <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1770505444/0A3A2553-2_egl6et.jpg" alt="Messages" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Split Section 1 - Fade + Rotate */}
      <motion.section 
        initial={{ opacity: 0, rotateX: 10 }}
        whileInView={{ opacity: 1, rotateX: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="section1" 
        style={{ paddingTop: '7rem' }}
      >
        <div className="split-section1">
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="split-image1"
          >
            <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1771527530/0A3A9081_cnmoh2.jpg" alt="community" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="split-content1"
          >
            <h2>Our Vision & Mission</h2>
            <p>To be a church where people experience the transforming power of God's love, growing in faith and making a lasting impact in our community and beyond.</p>
            <p>We exist to glorify God by making disciples of Jesus Christ through authentic worship, biblical teaching, genuine fellowship, and compassionate service.</p>
            <Link to="/vision-mission" className="card-link">Learn about our values</Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Banner - Zoom in */}
      <motion.section 
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="banner"
      >
        <h2>Join Us This Sunday</h2>
        <p>Experience inspiring worship, relevant teaching, and genuine community. Come as you are.</p>
        <button onClick={openModal} className="banner-cta">Service Times</button>
      </motion.section>

      {/* Split Section 2 - Slide up */}
      <motion.section 
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="section1"
      >
        <div className="split-section1 reverse1">
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="split-content1"
          >
            <h2>Connect & Get Involved</h2>
            <p>Life is better together. Whether you're looking to serve, join a small group, or simply connect with others, we have a place for you.</p>
            <p>Explore our various departments and ministries to find where you can make an impact and grow in your faith journey.</p>
            <Link to="/departments" className="card-link">Explore departments</Link>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="split-image1"
          >
            <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1770567382/0A3A7430_qfshm2.jpg" alt="get involved" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </motion.div>
        </div>
      </motion.section>

      {/* Service Times Section - Blur to focus */}
      {/* <motion.section 
        initial={{ opacity: 0, filter: "blur(10px)" }}
        whileInView={{ opacity: 1, filter: "blur(0px)" }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="section1"
      >
        <div className="section-header1">
          <h2 className="section-title1" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>Service Times</h2>
        </div>
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
          {[
            { title: "Sunday Worship", time: "11:00 AM", desc: "Join us for inspiring worship and biblical teaching" },
            { title: "Tuesday Bible Study", time: "7:00 PM", desc: "Dive deeper into God's Word with our community" },
            { title: "Youth Service", time: "Friday 6:30 PM", desc: "Dynamic gatherings for middle & high school students" },
          ].map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="card"
            >
              <div className="card-content">
                <h3 className="card-title">{service.title}</h3>
                <p className="card-text" style={{ fontSize: '2rem', fontWeight: 600, marginBottom: '0.5rem' }}>{service.time}</p>
                <p className="card-text">{service.desc}</p>
              </div>
            </motion.div>
          ))}
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="card" 
            style={{ background: 'var(--red-accent)', color: 'var(--white)' }}
          >
            <div className="card-content">
              <h3 className="card-title" style={{ color: 'var(--white)' }}>Watch Live</h3>
              <p className="card-text" style={{ fontSize: '2rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--white)' }}>Sundays 11:00 AM</p>
              <p className="card-text" style={{ color: 'rgba(255,255,255,0.8)' }}>Can't make it in person? Join us online via livestream</p>
              <Link to="/livestream" className="card-link" style={{ color: 'var(--white)', marginTop: '1rem', display: 'inline-flex' }}>Join livestream</Link>
            </div>
          </motion.div>
        </div>
      </motion.section> */}

      {/* Prayer Request Section - Slide from bottom */}
      <motion.section 
        initial={{ opacity: 0, y: 80 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="section1 last-section1" 
        style={{ background: 'var(--white)' }}
      >
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="section-header1" style={{ textAlign: 'center' }}>
            <h2 className="section-title1" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>Prayer Requests</h2>
            <p className="section-subtitle1" style={{ margin: '0 auto' }}>We believe in the power of prayer. Share your prayer request with us and our prayer team will lift you up in prayer.</p>
          </div>

          <motion.form 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className='section1-form'
            onSubmit={handlePrayerSubmit}
          >
            {/* Hidden subject field */}
            <input type="hidden" name="user_subject" value="Prayer Request" />
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--black)', fontWeight: 600, fontSize: '0.95rem' }}>Name</label>
              <input 
                type="text" 
                name="user_name"
                placeholder="Your name" 
                required
                style={{ width: '100%', padding: '1rem 1.25rem', border: '1px solid var(--gray-300)', borderRadius: '4px', fontSize: '1rem', fontFamily: 'inherit', transition: 'border-color 0.3s ease' }} 
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--black)', fontWeight: 600, fontSize: '0.95rem' }}>Email</label>
              <input 
                type="email" 
                name="user_email"
                placeholder="your@email.com" 
                required
                style={{ width: '100%', padding: '1rem 1.25rem', border: '1px solid var(--gray-300)', borderRadius: '4px', fontSize: '1rem', fontFamily: 'inherit', transition: 'border-color 0.3s ease' }} 
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--black)', fontWeight: 600, fontSize: '0.95rem' }}>Prayer Request</label>
              <textarea 
                name="user_message"
                placeholder="Share your prayer request..." 
                required
                style={{ width: '100%', padding: '1rem 1.25rem', border: '1px solid var(--gray-300)', borderRadius: '4px', fontSize: '1rem', fontFamily: 'inherit', minHeight: '150px', resize: 'vertical', transition: 'border-color 0.3s ease' }}
              ></textarea>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  name="confidential"
                  value="yes"
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }} 
                />
                <span style={{ color: 'var(--gray-600)', fontSize: '0.95rem' }}>I would like this prayer request to remain confidential</span>
              </label>
            </div>

            <button type="submit" className="prayer-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Prayer Request'}
            </button>
          </motion.form>
        </div>
      </motion.section>
      </div>

      {/* Service Times Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal} aria-label="Close modal">
              ×
            </button>
            <h2 className="modal-title">Service Times</h2>
            <div className="modal-services">
              <div className="modal-service-item">
                <div className="modal-service-icon">📖</div>
                <h3>Sunday School</h3>
                <p className="modal-service-time">10:30 AM</p>
                <p className="modal-service-desc">Dive deeper into God's Word with our community</p>
              </div>
              <div className="modal-service-item">
                <div className="modal-service-icon">⛪</div>
                <h3>Sunday Worship</h3>
                <p className="modal-service-time">11:00 AM</p>
                <p className="modal-service-desc">Join us for inspiring worship and biblical teaching</p>
              </div>
              <div className="modal-service-item">
                <div className="modal-service-icon">🧑‍🤝‍🧑</div>
                <h3>Youth Service</h3>
                <p className="modal-service-time">11:00 AM <br /><span>Every Last Sunday</span></p>
                <p className="modal-service-desc">Dynamic gatherings for middle & high school students</p>
              </div>
              <div className="modal-service-item modal-service-featured">
                <div className="modal-service-icon">💻</div>
                <h3>Watch Live</h3>
                <p className="modal-service-time">Sundays 11:00 AM</p>
                <p className="modal-service-desc">Can't make it in person? Join us online via livestream</p>
              </div>
            </div>
            <div className="modal-footer">
              <p className="modal-address">📍 211 E Main St, Middletown, DE 19709</p>
              <Link to="/visit" className="modal-cta" onClick={closeModal}>Plan Your Visit</Link>
            </div>
          </div>
        </div>
      )}

      {/* Prayer Request Popup */}
      <AnimatePresence>
        {popupMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="prayer-popup-overlay"
            onClick={closePopup}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className={`prayer-popup-box ${popupType}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popup-icon">
                {popupType === 'success' ? (
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" />
                    <path d="M14 24L20 30L34 16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" />
                    <path d="M16 16L32 32M32 16L16 32" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                )}
              </div>
              <p>{popupMessage}</p>
              <button onClick={closePopup}>Close</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;
